import styled, { css } from 'styled-components';

import Popover from '@mui/material/Popover';
import { BaseStyle, Button } from '@nhl/nhl-breakaway-ui';

import { WithHidden, WithIsStickyHeader } from '~/core/styleTypes';
import { SpecialEvent } from '~/core/tableColumns/scheduleTableColumns/enums';

import { SIZES } from './components/listView/components/dayOverview/styles';
import { getEventColor } from './components/listView/components/dayOverview/utils';

type WithEventType = { $eventType: SpecialEvent };

export const AdContainer = styled.div<WithHidden>`
    display: none;
    left: 0;
    min-height: 250px;
    max-width: 300px;
    position: relative;
    width: 100%;

    ${BaseStyle.Mixins.screenSizes.large} {
        display: block;
    }

    ${({ $isHidden }) =>
        $isHidden &&
        css`
            position: absolute; // a way to get the ad out of the flow of the page when it's not visible, so as to avoid a gap being rendered
            left: 100vw;
            width: 0;
        `};
`;

export const LinkContainer = styled.a<{ $isDisabled?: boolean; $isMedium?: boolean }>`
    position: relative;
    display: flex;
    width: ${({ $isMedium }) => ($isMedium ? 'initial' : '100%')};
    height: 48px;
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.Surface.border};
    border-radius: 8px;
    justify-content: center;
    align-items: center;
    background: ${({ $isDisabled, theme }) => ($isDisabled ? theme.Surface.disabled : 'initial')};

    && {
        svg {
            ${BaseStyle.Mixins.fontStyles.ui['24m']};
            fill: ${({ $isDisabled, theme }) => ($isDisabled ? theme.Text.disabled : theme.Text.secondary)};
            margin-left: 2px;

            ${BaseStyle.Mixins.screenSizes.medium} {
                fill: ${({ $isDisabled, theme }) => ($isDisabled ? theme.Text.disabled : theme.Text.primary)};
            }
        }
    }

    &:hover[href]:before {
        border-radius: 7px;
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0.08);
        content: '';
    }
`;

export const LinkText = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['14b']}
    height: 20px;
    width: 100%;

    && {
        line-height: 18px;
        justify-content: center;
    }
`;

export const MobileDaysContainer = styled.div<WithIsStickyHeader>`
    ${({ $isStickyHeader, theme }) =>
        $isStickyHeader &&
        css`
            ${BaseStyle.Mixins.utility.boxShadow(8, theme.Surface.shim, [BaseStyle.StyleDirections.down])};
            background: ${({ theme }) => theme.Surface.primary};
            // Set to 1500 b/c of Filter Dropdowns
            z-index: 1500;
        `};

    position: ${({ $isStickyHeader }) => ($isStickyHeader ? 'sticky' : 'initial')};
    top: 64px;

    padding: 8px 16px 0 16px;
    transform: translateX(-16px);
    width: calc(100% + 32px);
`;

export const OddsProviderWrapper = styled.div`
    position: relative;
    top: 8px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        display: none;
    }
`;

export const Controls = styled.div<WithIsStickyHeader>`
    background: ${({ theme }) => theme.Surface.primary};
    display: flex;
    gap: ${({ $isStickyHeader }) => ($isStickyHeader ? 0 : '24px')};
    padding-bottom: 0;

    @media print {
        display: none;
    }

    // The below is all for the sticky control header
    ${({ $isStickyHeader, theme }) =>
        $isStickyHeader &&
        css`
            ${BaseStyle.Mixins.utility.boxShadow(8, theme.Surface.shim, [BaseStyle.StyleDirections.down])}
            padding: 0 16px;
            transform: translateX(-16px);
            width: calc(100% + 32px);
            z-index: 2;

            ${BaseStyle.Mixins.screenSizes.medium} {
                position: sticky;
                top: -65px;
            }

            ${BaseStyle.Mixins.screenSizes.large} {
                position: sticky;
                top: -91px;

                padding: 0 24px;
                transform: translateX(-24px);
                width: calc(100% + 48px);
            }
        `}
`;

const FilterContainer = styled.div`
    ${BaseStyle.Mixins.gaps.medium};
    display: flex;
    flex: 1;
    flex-direction: column;
`;

export const SmallLeagueFilters = styled(FilterContainer)`
    padding-top: 16px;
`;

export const Control = styled(FilterContainer)<{
    $isClubPage?: boolean;
}>`
    // Since the day header is not part of the control container on mobile we add in padding
    padding-bottom: ${({ $isClubPage }) => ($isClubPage ? 0 : '8px')};

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.gaps.large}
        padding-bottom: initial;
    }

    ${BaseStyle.Mixins.screenSizes.large} {
        min-height: ${({ $isClubPage }) => ($isClubPage ? '250px' : 'none')};

        // On club pages we use the same gap for large screens as we do on medium
        ${({ $isClubPage }) => css`
            ${$isClubPage ? BaseStyle.Mixins.gaps.large : BaseStyle.Mixins.gaps.semiLarge}
        `};
    }
`;

export const Header = styled.h1<{ $isClubPage?: boolean }>`
    ${BaseStyle.Mixins.gaps.semiLarge}
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-bottom: ${({ $isClubPage }) => ($isClubPage ? '8px' : '0')};
`;

export const SelectContainer = styled.div`
    height: 45px;
`;

export const Filters = styled.div<{ $isStickyHeader?: boolean }>`
    display: flex;
    justify-content: space-between;
    visibility: ${({ $isStickyHeader }) => ($isStickyHeader ? 'hidden' : 'initial')};
`;

export const LeftHandFilters = styled.div`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    align-items: center;
    flex: 1;
    height: 48px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        align-items: initial;
        max-height: none;
    }

    .react-date-picker-console {
        margin-bottom: 0;
    }
`;

export const EventContainerSmall = styled.div<{ $isCardItem?: boolean }>`
    align-items: center;
    display: flex;

    ${({ $isCardItem }) =>
        $isCardItem &&
        css`
            padding-bottom: 8px;
            padding-left: 10px;
            padding-top: 4px;
        `}
`;

export const EventIndicatorSmall = styled.div<WithEventType>`
    background: ${({ $eventType, theme }) => getEventColor($eventType, theme)};
    border-radius: 8px;
    height: ${SIZES.eventIndicator.heightSmall}px;
    margin-right: 8px;
    width: 4px;
`;

const cardItemStyles = css`
    align-items: center;
    border-bottom: 1px solid ${({ theme }) => theme.Surface.border};
    display: flex;
    outline: none;
    padding: 10px 0 10px 10px;
    text-align: left;
`;

export const CardLinkItem = styled.a`
    ${cardItemStyles}
`;

export const CardButtonItem = styled.div`
    ${cardItemStyles}
`;

export const CardIcon = styled.div`
    display: flex;
    padding-right: 4px;

    svg {
        ${BaseStyle.Mixins.fontStyles.ui['24m']}
        fill: ${({ theme }) => theme.Utility.cta}}
    }
`;

export const CardDescription = styled.div<{ $isPlayoffSeriesPage?: boolean }>`
    ${BaseStyle.Mixins.fontStyles.desktop.cta};

    && {
        color: ${({ theme, $isPlayoffSeriesPage }) => ($isPlayoffSeriesPage ? theme.Text.primary : theme.Utility.cta)};
    }
`;

export const CardActionContainer = styled.div`
    display: flex;
    justify-content: end;
    flex: 1;

    svg {
        ${BaseStyle.Mixins.fontStyles.ui['24m']}
    }
`;

export const ActionPopover = styled(Popover)`
    .MuiPaper-root {
        border-radius: 4px;
        box-shadow: ${BaseStyle.Mixins.utility.fadedBoxShadow};
        max-width: 300px;
        width: 80vw;
        margin-left: -16px;
    }
`;

export const TabBarContainer = styled.div`
    padding-bottom: 24px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        padding-bottom: 0;
    }
`;

export const ButtonsContainer = styled.div`
    ${BaseStyle.Mixins.gaps.medium}
    display: flex;
    flex-direction: row;

    svg {
        ${BaseStyle.Mixins.fontStyles.ui['24m']};
        height: 48px;
        fill: ${({ theme }) => theme.Text.secondary};
    }

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.gaps.small}

        svg {
            fill: ${({ theme }) => theme.Text.primary};
        }
    }

    .print-button {
        width: 100%;
        height: 48px;
        padding: 12px 16px;
        justify-content: space-between;

        ${BaseStyle.Mixins.screenSizes.medium} {
            padding: 8px 12px;
            max-width: 48px;
        }
    }
`;

export const ClubScheduleButton = styled(Button)`
    ${BaseStyle.Mixins.gaps.extraSmall}
    height: 44px;
    max-width: 188px;
    width: 50%;

    svg {
        font-size: 20px;
    }
`;

export const ExpandFilterButton = styled(Button)`
    height: 30px;
`;
